import { trackEvent } from "analytics/posthog";

/**
 * "Started booking" for a booking that now exists (1NOW-11119): PostHog's funnel step, and Meta's
 * InitiateCheckout with eventID `ic-<booking id>`, which the server's InitiateCheckout repeats so
 * the two dedupe.
 *
 * Fired when the checkout page opens for a card booking, and when a Pay later booking is created,
 * since that path never opens the checkout page. Pay later does not send Purchase: that stays for
 * a payment that was actually taken.
 *
 * The value is the rental price without the security deposit, which is refunded and must not
 * inflate what the ad platforms optimise on.
 */
export const trackBookingStarted = (booking: any, car: any, currency?: string): void =>
  trackEvent("booking_started", {
    vehicle_id: car?.id,
    booking_id: booking?.id,
    value: booking?.final_price_without_security,
    currency,
  });

/**
 * 1NOW-11501: where the renter is in the booking, so a PostHog funnel on `booking_step_viewed` by `step`
 * shows where they leave. The order is the funnel's: `step_index` is the position here, and the five
 * steps of the booking sheet / desktop pages (steps.ts STEP_KEYS) sit at 1 to 5, as their `?step=` numbers.
 */
export const BOOKING_STEPS = [
  "car",
  "details",
  "protection",
  "extras",
  "review",
  "pay",
  "email_code",
  "pay_later_policies",
  "booking_page",
  "payment_success",
] as const;
export type BookingStepName = (typeof BOOKING_STEPS)[number];

export interface BookingStepProps {
  vehicle_id?: number | string;
  design?: "A" | "B";
  pay_mode?: "now" | "later";
  returning_renter?: boolean;
  /** The booking's number. Never its uuid (1NOW-10869): a string is dropped. */
  booking_id?: number;
}

/** Only these leave the page, whatever a caller passes: no email, phone, name or uuid can ride along (1NOW-11501). */
const pick = (p: BookingStepProps): BookingStepProps => {
  const out: BookingStepProps = {};
  if (typeof p.vehicle_id === "number" || typeof p.vehicle_id === "string") out.vehicle_id = p.vehicle_id;
  if (p.design) out.design = p.design;
  if (p.pay_mode) out.pay_mode = p.pay_mode;
  if (typeof p.returning_renter === "boolean") out.returning_renter = p.returning_renter;
  if (typeof p.booking_id === "number") out.booking_id = p.booking_id;
  return out;
};

export const trackBookingStep = (step: BookingStepName, props: BookingStepProps = {}): void =>
  trackEvent("booking_step_viewed", { step, step_index: BOOKING_STEPS.indexOf(step), ...pick(props) });

/** A refusal's own words can carry what the renter typed (an email, a phone, a booking link): those are masked. */
const redact = (message: string): string =>
  message
    .replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "[email]")
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "[id]")
    .replace(/\+?\d[\d\s().-]{6,}\d/g, "[number]")
    .slice(0, 200);

/** A step refused (card declined, wrong code, invalid ID, ...): why the renter left, not only where. */
export const trackBookingStepError = (step: BookingStepName, message: unknown, props: BookingStepProps = {}): void =>
  trackEvent("booking_step_error", {
    step,
    step_index: BOOKING_STEPS.indexOf(step),
    error: redact(String(message ?? "")),
    ...pick(props),
  });

/**
 * One `booking_step_viewed` per step per journey: a re-render, Back, or the review's Edit revisits a step
 * without counting it again. `seen` is the journey's memory (a ref in the steps' hook); `journey` names it
 * (the car), so another car is a new journey.
 */
export const trackBookingStepOnce = (
  seen: Set<string>,
  journey: string,
  step: BookingStepName,
  props: BookingStepProps = {},
): void => {
  const key = `${journey}:${step}`;
  if (seen.has(key)) return;
  seen.add(key);
  trackBookingStep(step, props);
};
